import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  Target,
  Gauge,
  CheckCircle2,
  Clock,
  XCircle,
} from "lucide-react";

const STATUS_LBL = {
  draft: { color: "bg-amber-100 text-amber-700", label: "Pending Review" },
  finalized: { color: "bg-emerald-100 text-emerald-700", label: "Settled" },
  reversed: { color: "bg-red-100 text-red-700", label: "Reversed" },
};

export default function MyMileage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/fuel/settlements/me");
        setData(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (!data) return null;

  const live = data.current_month_preview;
  const target = data.global_target_kmpl;

  return (
    <div className="space-y-4" data-testid="my-mileage-page">
      <div className="rounded-2xl bg-gradient-to-br from-bravura via-bravura-700 to-bravura-800 text-white p-5">
        <div className="text-[10px] uppercase tracking-widest text-bravura-100">Mileage Target</div>
        <div className="text-3xl font-bold mt-1 flex items-baseline gap-1.5" style={{ fontFamily: "Chivo" }}>
          {target} <span className="text-base font-normal text-bravura-100">km/L</span>
        </div>
        <p className="text-[11px] text-bravura-100 mt-2">
          More mileage = monthly bonus 🎉<br />Less = deduction
        </p>
      </div>

      {live ? (
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> Running this month
            </div>
            <span className="text-[10px] text-slate-400">{live.month}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Litres" value={`${live.total_litres} L`} />
            <Stat label="Km driven" value={live.actual_km.toLocaleString()} />
            <Stat label="Expected km" value={live.expected_km.toLocaleString()} />
            <Stat label="Diff (litres)" value={`${live.diff_litres > 0 ? "+" : ""}${live.diff_litres} L`}
              color={live.diff_litres > 0 ? "emerald" : live.diff_litres < 0 ? "rose" : "slate"} />
          </div>
          <div className={`rounded-xl p-4 text-center ${
            live.settlement_amount > 0 ? "bg-emerald-50 ring-1 ring-emerald-200" :
            live.settlement_amount < 0 ? "bg-red-50 ring-1 ring-red-200" :
            "bg-slate-50 ring-1 ring-slate-200"
          }`}>
            <div className="text-[10px] uppercase tracking-widest font-semibold mb-1">
              {live.settlement_amount > 0 ? "Projected Incentive" : live.settlement_amount < 0 ? "Projected Penalty" : "No Adjustment"}
            </div>
            <div className={`text-3xl font-bold ${
              live.settlement_amount > 0 ? "text-emerald-700" :
              live.settlement_amount < 0 ? "text-red-700" : "text-slate-500"
            }`} style={{ fontFamily: "Chivo" }}>
              {live.settlement_amount > 0 ? "+" : ""}{formatINR(live.settlement_amount)}
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              {live.settlement_amount > 0 ? <TrendingUp className="h-3 w-3 inline" /> : live.settlement_amount < 0 ? <TrendingDown className="h-3 w-3 inline" /> : null}
              {" "}Based on {live.refill_count} refill{live.refill_count !== 1 ? "s" : ""} so far
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-6 text-center text-sm text-slate-500">
          No refills logged this month yet. Add fuel to start tracking mileage.
        </div>
      )}

      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
          Past Settlements
        </div>
        {data.history.length === 0 ? (
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-6 text-center text-sm text-slate-500">
            No past settlements.
          </div>
        ) : (
          <div className="space-y-2">
            {data.history.map((s) => (
              <div key={s.id} className="rounded-xl bg-white ring-1 ring-slate-200 p-3" data-testid={`settlement-row-${s.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-sm font-bold text-slate-900">{s.month}</div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${STATUS_LBL[s.status]?.color}`}>
                    {STATUS_LBL[s.status]?.label}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-[11px] text-slate-500">
                    {s.actual_km} km / {s.total_litres} L · target {s.target_kmpl}
                  </div>
                  <div className={`font-bold ${
                    s.settlement_amount > 0 ? "text-emerald-700" :
                    s.settlement_amount < 0 ? "text-red-700" : "text-slate-500"
                  }`} style={{ fontFamily: "Chivo" }}>
                    {s.settlement_amount > 0 ? "+" : ""}{formatINR(s.settlement_amount)}
                  </div>
                </div>
                {s.status === "finalized" && s.payroll_ref?.posted && (
                  <div className="text-[10px] text-emerald-600 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Posted to next payslip
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color = "slate" }) {
  const map = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    rose: "text-red-700",
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`text-base font-bold ${map[color]}`} style={{ fontFamily: "Chivo" }}>
        {value}
      </div>
    </div>
  );
}
